import { Link } from "react-router-dom";
import { Instagram, Phone, Download } from "lucide-react";
import Logo from "./Logo";
import { EMAIL, INSTAGRAM, INSTAGRAM_URL, THREADS, THREADS_URL, SUPPORT_CATEGORIES } from "../lib/content";
import { renderWalletCard, renderPrintSheet, downloadHelplineCard } from "../lib/helplineCard";

export default function Footer() {
  return (
    <footer className="bg-[#090A0F] text-slate-400 border-t border-[#212636]">
      <div className="max-w-7xl mx-auto px-6 pt-14">
        <div className="bg-[#12141C] border border-[#212636] rounded-2xl p-6 sm:p-8 mb-14" data-testid="footer-support-block">
          <p className="mono-label text-[#EF4444] mb-1">Support — free, confidential, 24/7</p>
          <p className="text-sm text-slate-400 mb-5">This website and its AI tools are not monitored around the clock. The lines below are.</p>
          <div className="space-y-5">
            {SUPPORT_CATEGORIES.map((cat) => (
              <div key={cat.title} data-testid={`support-category-${cat.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="mono-label text-slate-500 mb-2">{cat.title}</p>
                <div className="flex flex-wrap gap-2">
                  {cat.lines.map(([label, desc, href]) => (
                    <a key={label} href={href} data-testid={`support-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group border border-[#212636] hover:border-[#EF4444] rounded-xl px-4 py-2.5 transition-colors">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-white"><Phone size={12} className="text-[#EF4444]" /> {label}</span>
                      <span className="block text-[10px] text-slate-500 mt-0.5">{desc}</span>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-[#212636] flex flex-wrap items-center gap-3">
            <p className="text-xs text-slate-500 sm:mr-auto max-w-md">Take the numbers with you — a wallet card for your pocket, or an A4 sheet to print and cut for hui handouts.</p>
            <button data-testid="helpline-card-download-wallet" onClick={async () => downloadHelplineCard(await renderWalletCard(), "break-the-cycle-helpline-wallet-card")}
              className="inline-flex items-center gap-1.5 bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs font-bold rounded-full px-4 py-2 transition-colors">
              <Download size={13} /> Wallet card
            </button>
            <button data-testid="helpline-card-download-sheet" onClick={async () => downloadHelplineCard(await renderPrintSheet(), "break-the-cycle-helpline-print-sheet-a4")}
              className="inline-flex items-center gap-1.5 border border-[#212636] hover:border-[#EF4444] text-slate-300 text-xs font-bold rounded-full px-4 py-2 transition-colors">
              <Download size={13} /> A4 print sheet
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Logo size={30} />
              <span className="font-display font-extrabold uppercase text-white text-sm">No To Meth</span>
            </div>
            <p className="text-sm leading-relaxed">Prevent the harm. Break the cycle. Save lives. Recovery is possible.</p>
          </div>
          <div>
            <h6 className="mono-label text-slate-500 mb-4">Campaign</h6>
            <div className="flex flex-col gap-2 text-sm">
              <a href="#mission" className="hover:text-white transition-colors">Mission &amp; vision</a>
              <a href="#poem" className="hover:text-white transition-colors">Shattered Glass</a>
              <a href="#stories" className="hover:text-white transition-colors">Lived experience</a>
              <a href="#plan" className="hover:text-white transition-colors">The plan</a>
            </div>
          </div>
          <div>
            <h6 className="mono-label text-slate-500 mb-4">Act</h6>
            <div className="flex flex-col gap-2 text-sm">
              <a href="#pledge" className="hover:text-white transition-colors">Sign the pledge</a>
              <a href="#support" className="hover:text-white transition-colors">Become a partner</a>
              <a href="#evidence" className="hover:text-white transition-colors">Ask the evidence</a>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy &amp; your rights</Link>
            </div>
          </div>
          <div>
            <h6 className="mono-label text-slate-500 mb-4">Follow &amp; contact</h6>
            <div className="flex flex-col gap-2 text-sm">
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-instagram-link"><Instagram size={13} /> Instagram {INSTAGRAM}</a>
              <a href={THREADS_URL} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-threads-link">Threads {THREADS}</a>
              <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[#212636] flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-600">
          <span>Ōtautahi Christchurch · Aotearoa New Zealand</span>
          <span>A community initiative — not a clinical treatment service. We connect people with appropriate professional support.</span>
        </div>
      </div>
    </footer>
  );
}
